import { BaseCrud } from '@/shared/models/cruds.models'
import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef, RowNode } from '@ag-grid-enterprise/all-modules'
import i18n from '@/lang/i18n'
import { ConfigurationCaseTypeLaboratoryResponse } from '@/modules/configurationModule/detail/local-cases/local-case-types/services/local-case-types.dto'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { initialLocalSampleTypesGridState, LocalSampleTypesGridState } from '@/modules/configurationModule/detail/local-samples/local-sample-types/components/local-sample-types-grid/local-sample-types-grid.model'
import { sampleTypeLaboratoriesConfigurationServices } from '@/modules/configurationModule/detail/local-samples/local-sample-types/services/local-sample-types.service'
import LocalSampleTypesModal from '@/modules/configurationModule/detail/local-samples/local-sample-types/components/local-sample-types-modal/local-sample-types-modal.vue'
import { ConfigurationSampleTypeLaboratoryCreateRequest, ConfigurationSampleTypeLaboratoryPatchInBatchRequest, ConfigurationSampleTypeLaboratoryResponse } from '@/modules/configurationModule/detail/local-samples/local-sample-types/services/local-sample-types.dto'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { StrictNullable } from '@/shared/types/app.types'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        LocalSampleTypesModal
    }
})

export default class LocalSampleTypesGrid extends BaseCrudServerSideComponent<ConfigurationSampleTypeLaboratoryResponse> implements BaseCrud {
    @Ref() readonly localSampleTypesModalWrapper!: any

    state: LocalSampleTypesGridState
    constructor() {
        super()
        this.state = initialLocalSampleTypesGridState()
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    get canUpdate(): boolean {
        const numberRowSelected: number = this.state.selectedRows.length
        return numberRowSelected === 1
    }

    getContextMenuItems(params:GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$t('configuration.actions.update').toString(),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => {
                    const nodeSelected:StrictNullable<RowNode> = params?.node
                    const selectedRow: ConfigurationCaseTypeLaboratoryResponse = nodeSelected?.data
                    return this.showModal(this.$t('configuration.actions.update') as string, selectedRow, this.localSampleTypesModalWrapper)
                }
            },
            {
                name: this.$t('configuration.actions.delete').toString(),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: () => this.deleteOperation(this.state.selectedRows)
            },
            {
                name: params?.node?.data?.active ? this.$t('configuration.actions.deactivate').toString() : this.$t('configuration.actions.activate').toString(),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$t('configuration.actions.activate').toString(),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(true, this.state.selectedRows)
            },
            {
                name: this.$t('configuration.actions.deactivate').toString(),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(false, this.state.selectedRows)
            }
        ]
    }

    async activateDeactivateSelection(option: boolean, selectionBatch: ConfigurationSampleTypeLaboratoryResponse[]): Promise<void> {
        const filteredRowActiveState = selectionBatch?.filter((row: ConfigurationSampleTypeLaboratoryResponse) => row.active !== option)
        const updatedFilteredRowActiveState: ConfigurationSampleTypeLaboratoryPatchInBatchRequest[] = filteredRowActiveState?.map((row: ConfigurationSampleTypeLaboratoryResponse) => {
            return {
                id: row.id,
                defaultDaysBeforeDisposal: row.defaultDaysBeforeDisposal,
                processingRequired: row.processingRequired,
                active: option
            }
        })
        if (updatedFilteredRowActiveState?.length) {
            this.$_updateLoading(true)
            const [err] = await this.$_toCall(sampleTypeLaboratoriesConfigurationServices.patchSampleTypeLaboratory(this.state.labId, updatedFilteredRowActiveState), true)
            this.$_updateLoading(false)
            if (err) {
                return
            }
            if (selectionBatch.length > 1) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 2, { elements: selectionBatch.length }))
            } else {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
            }
            this.reloadData()
        }
    }

    async createOperation(row: ConfigurationSampleTypeLaboratoryCreateRequest): Promise<void> {
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(sampleTypeLaboratoriesConfigurationServices.createSampleTypeLaboratory(this.state.labId, row), true)
        this.$_updateLoading(false)
        if (err) {
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.localSampleTypesModalWrapper.$slots.default[0].componentInstance.updateRow(response)
        this.state = {
            ...this.state,
            actionTitle: i18n.t('configuration.actions.update').toString()
        }
        this.$_showToastSuccess(this.$t('configuration.toastMessages.created')?.toString())
        this.reloadData()
    }

    async patchOperation(rows: ConfigurationSampleTypeLaboratoryPatchInBatchRequest[]): Promise<void> {
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(sampleTypeLaboratoriesConfigurationServices.patchSampleTypeLaboratory(this.state.labId, rows), true)
        this.$_updateLoading(false)
        if (err) {
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
        this.reloadData()
    }

    async deleteOperation(rows: ConfigurationSampleTypeLaboratoryResponse[]): Promise<void> {
        if (rows.length) {
            const selectedRowsSize:number = this.state?.selectedRows?.length
            const labelConfirmation:string = this.$t('configuration.grid.deleteConfirmation')?.toString()
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion:string = this.$t('configuration.grid.deleteQuestion')?.toString()
            const labelDeleteAction:string = this.$t('configuration.actions.delete')?.toString()
            const promise = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )
            if (promise) {
                this._deleteBatchOperation(rows)
            }
        }
    }

    private async _deleteBatchOperation(rows: ConfigurationSampleTypeLaboratoryResponse[]): Promise<void> {
        const rowsToDelete: EntityIdentifiersRequest = {
            entityIdentifiers: rows.map((template: ConfigurationSampleTypeLaboratoryResponse) => Number(template?.id))
        }
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(sampleTypeLaboratoriesConfigurationServices.deleteSampleTypeLaboratory(this.state.labId, rowsToDelete), true)
        this.$_updateLoading(false)
        if (err) {
            return
        } else if (response.length) {
            this.$_handleErrors(response)
            return
        }
        if (rows.length > 1) {
            this.$_showToastSuccess(
                this.$tc('configuration.toastMessages.deleted', 2, { elements: rows?.length })
            )
        } else {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
        }
        this.reloadData()
    }
}
